import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type { CollapsedSectionStatus } from "./CollapsedSectionRow.types";
import { fontSizes, lineHeights } from "@magicbi/ui-library";

const STATUS_BG: Record<Exclude<CollapsedSectionStatus, "none">, string> = {
  done: rawColors.accent.green500,
  attention: rawColors.accent.amber500,
  blocked: rawColors.accent.red500,
};

const STATUS_FG: Record<Exclude<CollapsedSectionStatus, "none">, string> = {
  done: rawColors.text.white,
  attention: rawColors.text.highEmphasis,
  blocked: rawColors.text.white,
};

/** F05 `.card.fold`: gap 12, padding 16/20, 1px stroke, radius 12 (xl). */
export const Root = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;

  &:focus-within {
    outline: 2px solid ${rawColors.brand.primary};
    outline-offset: 2px;
  }
`;

/** 18px round mark, F05 `.ico`. */
export const StatusMark = styled.span<{ $status: Exclude<CollapsedSectionStatus, "none"> }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: ${borderRadius.full};
  background: ${({ $status }) => STATUS_BG[$status]};
  color: ${({ $status }) => STATUS_FG[$status]};
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
`;

/** Keeps the title column aligned when there is no mark. */
export const StatusSpacer = styled.span`
  flex: none;
  width: 1.125rem;
`;

/** F05 `b`: 15/22 semibold, 120px column. */
export const Title = styled.b`
  flex: none;
  width: 7.5rem;
  ${typographyMixin("body", 2)}
  font-size: ${fontSizes["15"]};
  line-height: ${lineHeights["22"]};
  color: ${rawColors.text.highEmphasis};
`;

/** F05 `.v`: 14/22 medium, fills the row. */
export const Summary = styled.span`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** F05 link: 13/20 semibold, interactive colour. */
export const ChangeButton = styled.button`
  flex: none;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  ${typographyMixin("caption", 2)}
  font-size: ${fontSizes["13"]};
  line-height: ${lineHeights["20"]};
  color: ${rawColors.text.interactive};

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    text-decoration: underline;
  }
`;
